import { apiClient } from '@/api/client';
import { API_BASE_URL } from '@/lib/config';
import type { DataRoomService } from '@/lib/types/data-room';

/**
 * Why the backend refused a data-room launch (SSO Integration Specification
 * v1.1). The client never re-derives any of these from subscription or seat
 * state - two implementations of one rule diverge (`docs/specs/features/data-room/02-design.md` §12) - it
 * only renders the reason it is handed.
 *
 * `token_expired` and `token_replayed` are deliberately NOT in this union.
 * They are mechanism failures of a short-lived single-use credential rather
 * than refusals, and are answered by reissuing, not by a user-facing state.
 */
export type RefusalReason =
  | 'no_active_service'
  | 'seat_not_assigned'
  | 'company_email_required'
  | 'workspace_suspended'
  | 'entitlement_expired'
  | 'seat_limit_reached'
  | 'upstream_unavailable';

/**
 * The reasons this app has a rendered state for, as a runtime set. v1.2
 * (D-v4, both repos together): `subscription_inactive` became
 * `no_active_service`, since access now comes from an Issue Report or
 * Premium; `entitlement_expired` and `seat_limit_reached` are DAXE's own.
 */
const RENDERABLE_REFUSALS = new Set<string>([
  'no_active_service',
  'seat_not_assigned',
  'company_email_required',
  'workspace_suspended',
  'entitlement_expired',
  'seat_limit_reached',
  'upstream_unavailable',
]);

/**
 * Narrows a reason that arrived in a URL to one this app can render.
 *
 * The return from a refused handoff is a plain query parameter, so its value
 * is untrusted and is not necessarily one of the reasons above: it can be a
 * mechanism label with no user-facing state (`token_expired`,
 * `token_replayed`), a code DAXE sent that the backend did not recognise
 * (`daxe_error_unrecognised`), or anything at all if someone types the URL.
 *
 * All of those fall back to `upstream_unavailable`, which is the honest
 * answer: the data room could not be opened for a reason that is not about
 * the member's access or subscription, and it is the one state that offers a
 * retry rather than sending them to fix something that is not wrong.
 *
 * @param reason - the raw `reason` query parameter
 */
export function toRenderableRefusal(reason: string | null): RefusalReason {
  return reason && RENDERABLE_REFUSALS.has(reason)
    ? (reason as RefusalReason)
    : 'upstream_unavailable';
}

/**
 * The backend's entitlement decision. Carries a reason whenever it refuses,
 * and the service and its end date when it agrees, so the dashboard card and
 * the Data Room page can speak about an Issue Report or Premium. It
 * deliberately carries no handoff material.
 */
export type DataRoomEntitlement =
  | { entitled: true; service: DataRoomService; endsAt: string }
  | { entitled: false; reason: RefusalReason };

/**
 * What an approved launch hands back: where to post the handoff, and the
 * one-time ticket that authorises it.
 *
 * The ticket is opaque, single-use and 60-second, and authorises exactly this
 * one handoff. It is NOT the DAXE token - that is minted by, and never leaves,
 * the server-rendered handoff response - so holding it just long enough to put
 * it in a request body is in bounds. It must never be logged, rendered as
 * visible text, written to storage, or put in a URL.
 */
export interface DataRoomHandoffTicket {
  handoffUrl: string;
  handoffTicket: string;
}

/** Launch outcome: the handoff material, or the typed refusal. */
export type DataRoomLaunchResponse =
  ({ entitled: true } & DataRoomHandoffTicket) | { entitled: false; reason: RefusalReason };

/**
 * Wire contract with the backend handoff endpoint (SSO Integration
 * Specification v1.1). Renaming this breaks the handoff, so it is written once
 * here and asserted as a literal in the tests.
 */
const HANDOFF_TICKET_FIELD = 'handoff_ticket';

/**
 * Data-room resource for one startup. Split by resource type per ADR FE-1, so
 * no call site concatenates `/startups/` itself.
 *
 * @param startupId - the startup entity whose data room is being addressed
 * @param segment - the sub-resource (`entitlement`, `launch`)
 */
function dataRoomPath(startupId: string, segment: string): string {
  return `/startups/${startupId}/data-room/${segment}`;
}

/**
 * Whether this user may open this startup's data room right now. Gates the
 * launch affordance only: the launch itself re-checks authoritatively, so a
 * cached "entitled" can never let anyone through.
 *
 * @param startupId - the startup entity whose data room is being addressed
 */
export async function getDataRoomEntitlement(startupId: string): Promise<DataRoomEntitlement> {
  const { data } = await apiClient.get<DataRoomEntitlement>(dataRoomPath(startupId, 'entitlement'));
  return data;
}

/**
 * Asks the backend to start a handoff. It records the attempt with its reason
 * and answers with either the handoff material or the typed refusal. Goes
 * through `apiClient`, so the session bearer token authenticates it - which a
 * document navigation could not do.
 *
 * @param startupId - the startup entity whose data room is being opened
 */
export async function requestDataRoomLaunch(startupId: string): Promise<DataRoomLaunchResponse> {
  const { data } = await apiClient.post<DataRoomLaunchResponse>(dataRoomPath(startupId, 'launch'));
  return data;
}

/**
 * Resolves the backend's handoff path to an absolute URL on the API's origin.
 *
 * The backend returns a root-relative path (`/v1/startups/.../data-room/sso`)
 * because it is the one repository that owns that route's spelling. A form
 * `action` set to that path would resolve against THIS app's origin, and the
 * SPA and the API are separate origins in every deployed environment - so the
 * handoff would POST to the SPA host and never reach the endpoint that mints
 * the token. Resolving against `API_BASE_URL` fixes the origin while leaving
 * the path exactly as the backend spelled it, and works whether or not that
 * base carries the `/v1` prefix, since a leading-slash path replaces the
 * base's own path either way.
 *
 * @param handoffUrl - the root-relative path from an approved launch
 */
function absoluteHandoffUrl(handoffUrl: string): string {
  return new URL(handoffUrl, API_BASE_URL).toString();
}

/**
 * Performs the handoff: builds a form carrying the one-time ticket in its body
 * and submits it as the document navigation. The response is the
 * server-rendered auto-submitting form that POSTs to DAXE, so the DAXE token
 * is minted server-side and never reaches this app.
 *
 * The ticket travels in the request body, never in the URL - a query string
 * would put it in history, the referer header and every access log. The form
 * is hidden, holds the ticket only in a hidden input, and is removed again
 * immediately, so no ticket is ever rendered as text or left in the DOM.
 *
 * @param handoff - the URL and one-time ticket from an approved launch
 */
export function submitDataRoomHandoff({ handoffUrl, handoffTicket }: DataRoomHandoffTicket): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = absoluteHandoffUrl(handoffUrl);
  form.enctype = 'application/x-www-form-urlencoded';
  form.hidden = true;

  const ticketField = document.createElement('input');
  ticketField.type = 'hidden';
  ticketField.name = HANDOFF_TICKET_FIELD;
  ticketField.value = handoffTicket;
  form.appendChild(ticketField);

  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    // Never leave the ticket sitting in the DOM, including when submission
    // throws - the error itself is left to propagate.
    form.remove();
  }
}
